import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #upsellObserver = null;

  /** @type {number | undefined} */
  #upsellTimer;

  /** @type {boolean} */
  #upsellInjecting = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // Cart Upsell ("You May Also Need"): the drawer markup lives in a section
    // (header-actions) that can't be patched cheaply, so we inject the upsell
    // here on open and re-inject whenever the cart items morph.
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleUpsell);
    this.#upsellObserver = new MutationObserver(this.#scheduleUpsell);
    this.#upsellObserver.observe(this, { childList: true, subtree: true });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleUpsell);
    this.#upsellObserver?.disconnect();
    clearTimeout(this.#upsellTimer);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /* --------------------------------------------------------------------- */
  /* Cart Upsell injection                                                 */
  /* --------------------------------------------------------------------- */

  #root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  #esc(value) {
    return String(value == null ? '' : value).replace(
      /[&<>"]/g,
      (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch])
    );
  }

  // Debounced trigger used by both the open event and the morph observer.
  #scheduleUpsell = () => {
    clearTimeout(this.#upsellTimer);
    this.#upsellTimer = setTimeout(() => {
      this.#injectUpsell();
    }, 80);
  };

  async #injectUpsell() {
    const cfg = window.CartUpsellConfig;
    if (!cfg || !cfg.enabled) return;

    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog || !dialog.open) return;

    const content = dialog.querySelector('.cart-drawer__content');
    if (!content) return; // empty cart — nothing to upsell against

    // Already present (and not yet stripped by a morph) — leave it be.
    if (content.querySelector('cart-upsell-component')) return;
    if (this.#upsellInjecting) return;
    this.#upsellInjecting = true;

    try {
      this.#ensureUpsellAssets(cfg);

      // Representative product = most recently added line item.
      let productId = '';
      try {
        const res = await fetch(`${this.#root()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        if (!cart || !cart.item_count) return;
        const items = cart.items || [];
        productId = items.length ? String(items[items.length - 1].product_id) : '';
      } catch (e) {
        return;
      }
      if (!productId) return;

      // The drawer may have morphed (and the upsell stripped) while we awaited.
      const liveContent = dialog.querySelector('.cart-drawer__content');
      if (!liveContent || liveContent.querySelector('cart-upsell-component')) return;

      // Pick an intent that actually returns products (fall back to related).
      const intent = await this.#resolveIntent(productId, cfg.intent || 'related', cfg.limit || 6);
      if (!intent) return; // nothing to recommend at all — stay silent

      const host = this.#buildUpsell(cfg, productId, intent);
      const items = liveContent.querySelector('.cart-drawer__items');
      const summary = liveContent.querySelector('.cart-drawer__summary');
      const position = cfg.position || 'below_items';

      if (position === 'above_items' && items) {
        items.before(host);
      } else if (position === 'below_checkout' && summary) {
        summary.after(host);
      } else if (items) {
        items.after(host); // below_items / above_checkout: below the items, above the summary
      } else if (summary) {
        summary.before(host);
      } else {
        liveContent.appendChild(host);
      }
    } finally {
      this.#upsellInjecting = false;
    }
  }

  #ensureUpsellAssets(cfg) {
    if (cfg.cssUrl && !document.querySelector('link[data-cart-upsell-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cfg.cssUrl;
      link.setAttribute('data-cart-upsell-css', '');
      document.head.appendChild(link);
    }
    if (cfg.jsUrl && !document.querySelector('script[data-cart-upsell-js]')) {
      const script = document.createElement('script');
      script.src = cfg.jsUrl;
      script.type = 'module';
      script.setAttribute('data-cart-upsell-js', '');
      document.head.appendChild(script);
    }
  }

  async #resolveIntent(productId, intent, limit) {
    const cfg = window.CartUpsellConfig || {};
    const base = cfg.recommendationsUrl || `${this.#root()}recommendations/products`;

    const count = async (it) => {
      try {
        const url = `${base}.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(
          limit
        )}&intent=${encodeURIComponent(it)}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return 0;
        const data = await res.json();
        return (data && data.products && data.products.length) || 0;
      } catch (e) {
        return 0;
      }
    };

    if (await count(intent)) return intent;
    if (intent !== 'related' && (await count('related'))) return 'related';
    return null;
  }

  #buildUpsell(cfg, productId, intent) {
    const layout = cfg.layout || 'carousel';
    const heading = cfg.heading || '';
    const limit = cfg.limit || 6;
    const recUrl = cfg.recommendationsUrl || `${this.#root()}recommendations/products`;
    const showDots = cfg.showDots !== false;

    const host = document.createElement('cart-upsell-component');
    host.id = 'cart-upsell-drawer';
    host.className = `cart-upsell cart-upsell--${layout} cart-upsell--hover-lift`;
    host.setAttribute('aria-label', heading);
    host.setAttribute('data-cart-add-url', cfg.cartAddUrl || `${this.#root()}cart/add`);
    host.setAttribute('data-show-arrows', String(cfg.showArrows !== false));
    host.setAttribute('data-show-dots', String(showDots));

    // The drawer summary uses `align-items: center`, and flex parents can shrink
    // a child to its content width — which collapses the cards (titles wrap one
    // character per line). Force the host to span the full drawer width.
    host.style.alignSelf = 'stretch';
    host.style.width = '100%';
    host.style.boxSizing = 'border-box';

    const arrows =
      layout === 'carousel'
        ? `<div class="cart-upsell__arrows" aria-hidden="false">
             <button type="button" class="cart-upsell__arrow" data-cu-prev aria-label="${this.#esc(
               cfg.prevLabel || 'Previous'
             )}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
             <button type="button" class="cart-upsell__arrow" data-cu-next aria-label="${this.#esc(
               cfg.nextLabel || 'Next'
             )}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
           </div>`
        : '';

    const dots =
      layout === 'carousel' && showDots
        ? `<div class="cart-upsell__dots" data-cu-dots role="tablist" aria-label="${this.#esc(heading)}"></div>`
        : '';

    host.innerHTML = `
      <div class="cart-upsell__header">
        <h3 class="cart-upsell__title">${this.#esc(heading)}</h3>
      </div>
      <div class="cart-upsell__nav">
        ${arrows}
        <cart-upsell-recommendations
          data-url="${this.#esc(recUrl)}"
          data-section-id="cart-upsell-recommendations"
          data-product-id="${this.#esc(productId)}"
          data-intent="${this.#esc(intent)}"
          data-limit="${this.#esc(limit)}"
        >
          <ul class="cart-upsell__track cart-upsell__skeletons" data-cu-track aria-busy="true">
            <li class="cart-upsell__skeleton" aria-hidden="true"></li>
            <li class="cart-upsell__skeleton" aria-hidden="true"></li>
            <li class="cart-upsell__skeleton" aria-hidden="true"></li>
          </ul>
        </cart-upsell-recommendations>
      </div>
      ${dots}
    `;
    return host;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
